import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { Meta } from '@angular/platform-browser';
import { SwUpdate } from '@angular/service-worker';
import { setTag } from '@sentry/angular';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { PREVENT_AUTH_HEADER } from '../auth/auth.interceptor';
import { CoreProvider } from '../core.provider';
import { UploadService } from '../match-videos/upload.service';

/** What `assets/version.json` looks like. The platform keys are stamped by grunt. */
interface VersionManifest {
  version: string;
  platform?: {
    pwa_version?: string;
    ios_version?: string;
    android_version?: string;
    desktop_runtime_version?: string;
  };
}

/** How often to ask whether a newer build has been deployed. */
const CHECK_INTERVAL_MS = 60 * 1000;

/**
 * Knows which version is running and whether a newer one is waiting.
 *
 * The running version comes from `<meta name="version">` in `index.html`, which grunt
 * stamps from `package.json` — there is no generated `version.ts` and no
 * `environment.version` to read instead.
 *
 * `updateable` is driven **only** by the service worker's `VERSION_READY` event, never
 * by the `version.json` comparison. The distinction matters: `version.json` flips the
 * moment a deploy lands, but the new bundle is not on the device yet, and offering an
 * update that has to download several MB first turns one click into a blank screen.
 * `VERSION_READY` means it is already downloaded, so activating it is instant.
 * `version.json` is still polled, because it is what tells the running tab to go and
 * look; it just reports rather than decides.
 */
@Injectable({ providedIn: 'root' })
export class VersionService {
  private core!: CoreProvider;

  /** The version this tab is running. */
  readonly current$ = new BehaviorSubject<string>('');
  /**
   * The newest version the server advertises, once known. Nothing renders it — it is
   * what you read off `window.core.versionService` to tell "no update was published"
   * apart from "one was, and this tab never picked it up".
   */
  readonly latest$ = new BehaviorSubject<string>('');
  /** True once a new build is downloaded and one reload away. */
  readonly updateable$ = new BehaviorSubject<boolean>(false);

  private checkHandle: any;

  constructor(
    private readonly meta: Meta,
    private readonly http: HttpClient,
    private readonly swUpdate: SwUpdate,
    private readonly upload: UploadService,
  ) {
    this.swUpdate.versionUpdates.pipe(filter((e) => e.type != 'NO_NEW_VERSION_DETECTED')).subscribe((event) => {
      if (event.type == 'VERSION_DETECTED') {
        console.debug('New version detected, downloading', event);
        return;
      }
      // VERSION_READY, or VERSION_INSTALLATION_FAILED — in which case a reload is
      // still the way out, since the worker will retry the install from scratch.
      console.debug('New version ready', event);
      this.updateable$.next(true);
    });
  }

  /** Convenience for templates and Sentry: the running version, or a placeholder. */
  get current(): string {
    return this.current$.value || '⏣.⏣.⏣';
  }

  get updateable(): boolean {
    return this.updateable$.value;
  }

  initCore(core: CoreProvider) {
    this.core = core;

    const version = this.meta.getTag('name="version"')?.content ?? '';
    this.current$.next(version);
    if (version) setTag('appVersion', version);

    this.startPolling();
  }

  startPolling(intervalMs = CHECK_INTERVAL_MS) {
    this.stopPolling();
    this.checkHandle = setInterval(() => void this.checkForUpdates(), intervalMs);
    void this.checkForUpdates();
  }

  stopPolling() {
    if (this.checkHandle) {
      clearInterval(this.checkHandle);
      this.checkHandle = undefined;
    }
  }

  /**
   * Asks the worker to look for a new build, and reads `version.json` for reporting.
   * Never throws: this runs on a timer and a flaky network is not worth a Sentry event.
   */
  async checkForUpdates(): Promise<void> {
    if (this.swUpdate.isEnabled) {
      await this.swUpdate.checkForUpdate().catch((error) => console.warn('Update check failed', error));
    }

    try {
      const manifest = await firstValueFrom(
        this.http.get<VersionManifest>('assets/version.json', {
          headers: {
            // The interceptor would otherwise reach for CoreProvider, which may still
            // be constructing this very service.
            [PREVENT_AUTH_HEADER]: 'true',
            // `ngsw-config.json` already keeps this file out of the cached `/assets/**`,
            // but the worker deciding that is the *old* one still installed on the
            // device — the one that shipped before the exclusion existed.
            'ngsw-bypass': 'true',
          },
        }),
      );
      this.latest$.next(manifest.platform?.pwa_version ?? manifest.version);
    } catch (error) {
      console.warn('Could not read assets/version.json', error);
    }
  }

  /**
   * Swaps in the downloaded build and reloads.
   *
   * Refuses while an upload is running: those are multipart uploads of a several-GB
   * match held in the page, so a reload throws away everything sent so far.
   */
  async update(): Promise<void> {
    if (!this.updateable) return;

    if (this.upload.progress$.value) {
      await this.core.errorToast(
        undefined,
        this.core.translate.instant(_('Cannot update while a match is uploading. Wait for it to finish and try again.')),
      );
      return;
    }

    try {
      await this.swUpdate.activateUpdate();
    } catch (error) {
      // The worker could not swap versions, but the new one is on disk, so a plain
      // reload still picks it up.
      console.error('Could not activate the update', error);
    }
    location.reload();
  }
}
